import { useRef, useEffect } from 'react';

import { loadModules } from 'esri-loader';

import styled from '@emotion/styled/macro';

import { FormattedMessage } from 'react-intl';

import { extentMap, getPowersimmLayerUrl } from '../../services/portfolios';

import { ReactComponent as ExpandFullscreenIcon } from '../../icons/expand-fullscreen.svg';
import { ReactComponent as CollapseFullscreenIcon } from '../../icons/collapse-fullscreen.svg';

import { Button, useDisclosure } from '@chakra-ui/react';
import MapDrawer from './MapDrawer';
import SideDrawer from '../utils/SideDrawer';

const getLayerOrder = (title) => {
    const orderArr = [
        'ISO MARKET POINTS',
        'TRANSMISSION LINES',
        'POWERPLANTS',
        'PLANNED STORAGE & SOLAR PROJECTS',
        'ELECTRIC RETAIL SERVICE TERRITORIES',
        'ASCEND ISO FORECASTING SUBREGIONS',
        'PLANNED RETIREMENTS',
        'RETIRED GENERATORS',
        'PLANNED GENERATORS',
        'QUALIFIED OPPORTUNITY ZONES',
        'MEDIAN HOME VALUE',
        'SUBSTATIONS',
    ];

    let order = orderArr.findIndex((elem) => title.toUpperCase().includes(elem));
    return order > -1 ? order : 100;
};

const PortfolioMap = ({ selected, mapExpanded, toggleMap, onPowersimLayerLoaded }) => {
    const map = useRef();
    const view = useRef();
    const basemapToggle = useRef();
    const searchWidget = useRef();
    const mapDiv = useRef(null);
    const filterDrawer = useDisclosure();

    const powersimmLayer = useRef();

    useEffect(() => {
        extentMap(selected, powersimmLayer.current, view.current);

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selected]);

    const allLayers = useRef([
        {
            id: 'powerplant_node',
            layerOrder: getLayerOrder('Powerplants'),
            childrenNodes: [],
            title: 'Powerplants',
            fakeNode: true,
            minScale: 12000000,
            maxScale: 0,
            url: '',
            visible: false,
            description:
                'This layer represents electric power plants. Power plants are all the land and land rights, structures and improvements, boiler or reactor vessel equipment, engines and engine-driven generators, turbo generator units, accessory electric equipment, and miscellaneous power plant equipment are grouped together for each individual facility. Included are the following plant types: hydroelectric dams, fossil fuel (coal, natural gas, or oil), nuclear, solar, wind, geothermal, and biomass.',
        },
        {
            id: 'iso_node',
            layerOrder: getLayerOrder('ISO Market Points'),
            childrenNodes: [],
            title: 'ISO Market Points',
            fakeNode: true,
            minScale: 0,
            maxScale: 0,
            url: '',
            visible: true,
            description:
                'This layer represents pricing nodes in each of the ISOs. Locations are not available for all nodes, so this displays only nodes with coordinates available. Locations match the precision of the underlying source, primarily EIA and ISO publications.',
        },
        {
            id: 'planned_storage_node',
            layerOrder: getLayerOrder('Planned Storage & Solar Projects'),
            childrenNodes: [],
            title: 'Planned Storage & Solar Projects',
            fakeNode: true,
            minScale: 0,
            maxScale: 0,
            url: '',
            visible: false,
            description: '',
        },
    ]);

    const addLayers = (layers) => {
        const oldLayers = allLayers.current.map((elem) => elem.id);
        const missingLayers = layers
            .filter((elem) => !oldLayers.includes(elem.id))
            .map((elem) => {
                elem.layerOrder = elem.layerOrder !== undefined ? elem.layerOrder : getLayerOrder(elem.title);
                return elem;
            });

        if (missingLayers.length > 0) {
            allLayers.current = [...allLayers.current, ...missingLayers];
        }
    };

    const addPowersimmLayer = async () => {
        const url = await getPowersimmLayerUrl();
        loadModules(['esri/layers/FeatureLayer']).then(([FeatureLayer]) => {
            const powerSimmLayer = new FeatureLayer({
                url: url,
            });
            powerSimmLayer.powerSimmLayer = true;
            powerSimmLayer.layerOrder = -1;
            map.current.add(powerSimmLayer);
        });
    };

    const loadMap = () => {
        loadModules(['esri/WebMap', 'esri/views/MapView', 'esri/widgets/BasemapToggle', 'esri/widgets/Search'])
            .then(async ([WebMap, MapView, BasemapToggle, Search]) => {
                map.current = new WebMap({
                    basemap: 'arcgis-topographic',
                    portalItem: {
                        id: process.env.REACT_APP_ARCGIS_PORTAL_ID,
                    },
                });

                view.current = new MapView({
                    container: mapDiv.current,
                    map: map.current,
                    center: [-70, 25],
                    zoom: 4,
                });

                basemapToggle.current = new BasemapToggle({
                    view: view.current,
                    nextBasemap: 'arcgis-imagery',
                });

                searchWidget.current = new Search({
                    view: view.current,
                });

                searchWidget.current.allSources.on('change', (e) => {
                    let arcGeo = searchWidget.current.allSources.findIndex(
                        (elem) => elem.name === 'ArcGIS World Geocoding Service'
                    );
                    if (arcGeo > -1) {
                        searchWidget.current.allSources.removeAt(arcGeo);
                    }
                });

                view.current.on('layerview-create', (e) => {
                    if (e.layer.powerSimmLayer) {
                        if (powersimmLayer.current === undefined) {
                            powersimmLayer.current = e.layer;
                            onPowersimLayerLoaded(e.layer, map.current, view.current);
                            extentMap(selected, powersimmLayer.current, view.current);
                            map.current.reorder(e.layer, 100);
                        }
                    }

                    const layer = allLayers.current.findIndex((elem) => e.layer.id === elem.id);
                    if (layer > -1) {
                        allLayers.current[layer].fullyLoaded = true;
                    }
                });

                if (powersimmLayer.current === undefined) {
                    addPowersimmLayer();
                }

                map.current.layers.on('change', (event) => {
                    if (event.added.length > 0) {
                        addLayers([...event.added]);
                    }
                });

                view.current.ui.add(basemapToggle.current, 'bottom-right');
                view.current.ui.add(searchWidget.current, 'top-left');
                view.current.popup.defaultPopupTemplateEnabled = true;
            })
            .catch((err) => console.error(err));
    };

    useEffect(() => {
        loadMap();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        <MapContainer isExpanded={mapExpanded}>
            <MapComponent ref={mapDiv} />

            <ActionButtonContainer>
                <ToggleButton
                    leftIcon={mapExpanded ? <CollapseFullscreenIcon /> : <ExpandFullscreenIcon />}
                    size="sm"
                    type="button"
                    onClick={toggleMap}
                >
                    <FormattedMessage
                        id={mapExpanded ? 'portfolio_map_expand_active_button' : 'portfolio_map_expand_inactive_button'}
                    />
                </ToggleButton>

                <FilterButton type="button" variant="secondary" onClick={filterDrawer.onOpen} size="sm">
                    <FormattedMessage id="portfolio_map_filters_button" />
                </FilterButton>
            </ActionButtonContainer>

            {filterDrawer.isOpen && (
                <SideDrawer
                    isOpen
                    size="map_md"
                    onClose={filterDrawer.onClose}
                    header={<FormattedMessage id="portfolio_map_filters_heading" />}
                    variant="mapDrawer"
                    trapFocus={false}
                    content={<MapDrawer layers={allLayers.current} map={map.current} view={view.current} />}
                    placement="left"
                />
            )}
        </MapContainer>
    );
};
const ActionButtonContainer = styled.div`
    position: absolute;
    top: 10px;
    right: 10px;
`;

const ToggleButton = styled(Button)`
    text-transform: capitalize;
    padding: 10px 10px;
    background: var(--chakra-colors-gray-500);

    span {
        margin-right: 0;
    }

    span svg path {
        fill: var(--chakra-colors-white);
    }

    &:hover span svg path {
        fill: var(--chakra-colors-blue-400);
    }

    &:hover {
        background: var(--chakra-colors-white);
        color: var(--chakra-colors-blue-400);
    }
`;

const FilterButton = styled(Button)`
    text-transform: capitalize;
    padding: 10px 10px;
    margin-left: 10px;
    background: var(--chakra-colors-gray-500);
    color: var(--chakra-colors-white);

    span {
        margin-right: 0;
    }

    span svg path {
        fill: var(--chakra-colors-white);
    }

    &:hover span svg path {
        fill: var(--chakra-colors-blue-400);
    }

    &:hover {
        background: var(--chakra-colors-white);
        color: var(--chakra-colors-blue-400);
    }
`;
const MapContainer = styled('div', { shouldForwardProp: (prop) => prop !== 'isExpanded' })`
    position: relative;
    min-height: ${(props) => (props.isExpanded ? 'calc(100vh - 120px)' : '300px')};
    overflow: ${(props) => props.isExpanded && 'hidden'};

    &:focus-within {
        &:after {
            content: '';
            box-sizing: border-box;
            position: absolute;
            z-index: 999;
            top: 0;
            left: 0;
            height: 100%;
            width: 100%;
            outline: auto 10px var(--chakra-ring-color);
            outline-offset: -3px;
            pointer-events: none;
            overflow: hidden;
        }
    }

    .esri-ui-top-left {
        display: flex;
        flex-direction: row;
    }

    .esri-search {
        margin-left: 10px;
    }
`;

const MapComponent = styled.div`
    height: 100%;

    .esri-view-surface--inset-outline:focus {
        &:after {
            outline: none;
        }
    }

    .esri-zoom .esri-widget--button:first-of-type {
        margin-bottom: 2px;
    }
`;

export default PortfolioMap;
